import { Form, notification } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectField } from 'src/components/Form/SelectField';
import { ValidationRules } from 'src/components/Form/ValidationRules';
import { Modal } from 'src/components/Modal/Modal';
import { PartnerDto, ProductDto } from 'src/generated-api-client';
import { ProductDetails } from 'src/pages/LoanApplicationDetailsPage/components/ProductDetails';
import { LoanApplicationsStore } from 'src/stores/LoanApplicationsStore/LoanApplicationsStore';
import { PartnersStore } from 'src/stores/PartnersStore/PartnersStore';
import { OptionType } from 'src/stores/ResourceStore/ResourceStore';
import { ConstraintViolationError } from 'src/utils/ErrorDataHelper';
import { FormHelper } from 'src/utils/FormHelper';
import { addModalRenderer, ModalRendererProps } from 'src/utils/ModalRenderer';

type ReassignApplicationDialogProps = ModalRendererProps<{
    applicationId: string | number;
}>;

const ReassignApplicationDialogFn = observer(
    ({
        applicationId,
        onSuccess,
        ...props
    }: ReassignApplicationDialogProps) => {
        const { t } = useTranslation();
        const [form] = Form.useForm<ReassignApplicationFormValues>();
        const [selectedPartner, setSelectedPartner] = useState<PartnerDto>();
        const [selectedProduct, setSelectedProduct] = useState<ProductDto>();
        useEffect(() => {
            PartnersStore.loadList();

            return () => {
                PartnersStore.listLoader.reset();
                store.assignProductLoader.reset();
            };
        }, []);

        const productOptions = useMemo(() => {
            return selectedPartner?.products?.map<OptionType<ProductDto>>(
                (product) => {
                    return {
                        label: product.name,
                        value: product.id,
                        item: product,
                    };
                },
            );
        }, [selectedPartner]);

        const fields = FormHelper.getFieldDataItems(
            store.assignProductLoader.errorData as ConstraintViolationError,
        );

        const onFinish = async (values: ReassignApplicationFormValues) => {
            await store.assignProduct(String(applicationId), values.productId!);
            if (!store.assignProductLoader.hasError) {
                notification.success({
                    message: t('App.ReassignApplicationDialog.ReassignSuccess'),
                });
                onSuccess?.();
            } else {
                return Promise.reject();
            }
        };

        return (
            <Modal
                form={form}
                title={t('App.ReassignApplicationDialog.Title')}
                okText={t('App.ReassignApplicationDialog.Assign')}
                isLoading={store.assignProductLoader.isLoading}
                formProps={{ fields }}
                onFinish={onFinish}
                {...props}
            >
                <SelectField
                    name={formNames.getPath('partnerId')}
                    label={t('App.ReassignApplicationDialog.Partner')}
                    rules={[ValidationRules.required]}
                    options={PartnersStore.partnerAsOptions}
                    isLoading={PartnersStore.listLoader.isLoading}
                    selectProps={{
                        onSelect: (_, selectedItem) => {
                            setSelectedPartner(
                                (selectedItem as OptionType<PartnerDto>).item,
                            );
                            setSelectedProduct(undefined);
                        },
                    }}
                />
                {selectedPartner && (
                    <SelectField
                        name={formNames.getPath('productId')}
                        label={t('App.ReassignApplicationDialog.Product')}
                        rules={[ValidationRules.required]}
                        options={productOptions}
                        selectProps={{
                            onSelect: (_, selectedItem) => {
                                setSelectedProduct(
                                    (selectedItem as OptionType<ProductDto>)
                                        .item,
                                );
                            },
                        }}
                    />
                )}
                {selectedProduct && selectedPartner && (
                    <ProductDetails
                        product={selectedProduct}
                        partner={selectedPartner}
                    />
                )}
            </Modal>
        );
    },
);

type ReassignApplicationFormValues = {
    partnerId?: string;
    productId?: string;
};

const formNames =
    FormHelper.createNameProvider<ReassignApplicationFormValues>();
const store = LoanApplicationsStore;

export const ReassignApplicationDialog = addModalRenderer(
    ReassignApplicationDialogFn,
);
